import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast"; // Corrected path for shadcn/ui
import { uploadProfileAssetToStorage } from "@/integrations/supabase/storage";
import { markMessagesAsRead, deleteChat } from "@/lib/actions";
import { ChatPreview, Message, Profile } from "@/components/messages/types";

// Hook for fetching chat list
const useChatList = (user: any, isAuthenticated: boolean) => {
  const queryClient = useQueryClient();

  const { data: chatsData, isLoading: isLoadingChats } = useQuery({
    queryKey: ["chats", user?.id],
    queryFn: async (): Promise<ChatPreview[]> => {
      if (!user) return [];

      // Fetch chats with listings
      const { data: rawChats, error: chatsError } = await supabase
        .from("chats")
        .select("*, listings(id, title, images, user_id)")
        .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
        .order("created_at", { ascending: false }); // Changed updated_at to created_at

      if (chatsError) {
        console.error("Error fetching chats:", chatsError);
        throw chatsError;
      }
      if (!rawChats || rawChats.length === 0) return [];

      // Collect all other user IDs to fetch their profiles in one go
      const otherUserIds = Array.from(new Set(
        rawChats.map(chat => chat.buyer_id === user.id ? chat.seller_id : chat.buyer_id).filter(id => id)
      ));

      let profilesMap = new Map<string, Partial<Profile>>();
      if (otherUserIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("id, username, full_name, profile_image")
          .in("id", otherUserIds);
        if (profilesError) console.error("Error fetching profiles:", profilesError);
        else profilesData?.forEach(p => profilesMap.set(p.id, p));
      }

      // Process each chat to enrich with details like last message, unread count, and other user profile
      const chatDetailsPromises = rawChats.map(async (chat) => {
        // Fetch last message for the chat
        const { data: lastMessageData, error: lastMessageError } = await supabase
          .from("messages")
          .select("content, created_at")
          .eq("chat_id", chat.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        // Handle potential error where no message exists (PGRST116)
        if (lastMessageError && lastMessageError.code !== 'PGRST116') {
          console.error(`Error fetching last message for chat ${chat.id}:`, lastMessageError);
        }

        // Fetch unread message count for the chat
        const { count, error: unreadError } = await supabase
          .from("messages")
          .select("*", { count: "exact", head: true })
          .eq("chat_id", chat.id)
          .eq("is_read", false)
          .not("sender_id", "eq", user.id);

        if (unreadError) {
          console.error(`Error fetching unread count for chat ${chat.id}:`, unreadError);
        }

        const otherUserId = chat.buyer_id === user.id ? chat.seller_id : chat.buyer_id;
        const profile = profilesMap.get(otherUserId) || { username: "Unknown User", full_name: "Unknown User" };
        
        // Ensure chat.created_at and chat.updated_at are accessed carefully due to potential typing issues with rawChats
        // Use 'as any' if TypeScript incorrectly infers these properties are missing, but ensure they exist in the DB.
        const chatCreatedAt = (chat as any).created_at || new Date().toISOString(); // Fallback for safety
        const chatUpdatedAt = (chat as any).updated_at || chatCreatedAt; // Fallback for safety. If DB chat.updated_at is undefined, this uses created_at

        return {
          id: chat.id,
          listing_id: chat.listing_id,
          listing_title: chat.listings?.title,
          listing_image: chat.listings?.images?.[0] || "",
          other_user_id: otherUserId,
          other_user_name: profile.full_name || profile.username || "Përdorues i Panjohur",
          other_user_profile_image: profile.profile_image,
          last_message: lastMessageData?.content || "Asnjë mesazh",
          created_at: chatCreatedAt, 
          last_message_at: lastMessageData?.created_at || chatUpdatedAt, 
          updated_at: chatUpdatedAt, // This will be chatCreatedAt if DB updated_at is missing
          unread_count: count || 0,
        };
      });

      const resolvedChatDetails = await Promise.all(chatDetailsPromises);
      // Sort chats by last message time or chat update time (which falls back to creation time)
      return resolvedChatDetails.sort((a, b) =>
        new Date(b.last_message_at || b.updated_at).getTime() - new Date(a.last_message_at || a.updated_at).getTime()
      );
    },
    enabled: !!user && isAuthenticated,
    refetchInterval: 30000, 
    staleTime: 15000,    
  });

  const invalidateChatList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["chats", user?.id] });
  }, [queryClient, user?.id]);

  return { chatsData, isLoadingChats, invalidateChatList };
};

// Hook for managing selected chat and related data
const useSelectedChat = (chats: ChatPreview[]) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const chatIdFromUrl = searchParams.get("chat");
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);

  useEffect(() => {
    if (chatIdFromUrl) {
      const numericChatId = Number(chatIdFromUrl);
      if (!isNaN(numericChatId)) {
        setSelectedChatId(numericChatId);
      }
    } else {
      setSelectedChatId(null); // Deselect if chat ID is removed from URL
    }
  }, [chatIdFromUrl]);

  // Memoize current selected chat details to avoid re-computation
  const currentSelectedChatDetails = useMemo(() => {
    if (!selectedChatId) return null;
    return chats.find(chat => chat.id === selectedChatId);
  }, [selectedChatId, chats]);

  // Memoize other user profile based on selected chat
  const otherUserProfile = useMemo(() => {
    if (!currentSelectedChatDetails || !currentSelectedChatDetails.other_user_id) return null;
    return {
      id: currentSelectedChatDetails.other_user_id,
      username: currentSelectedChatDetails.other_user_name,
      full_name: currentSelectedChatDetails.other_user_name,
      profile_image: currentSelectedChatDetails.other_user_profile_image, // Use added field
    };
  }, [currentSelectedChatDetails]);

  // Memoize current listing based on selected chat
  const currentListing = useMemo(() => {
    if (!currentSelectedChatDetails || !currentSelectedChatDetails.listing_id) return null;
    return {
      id: currentSelectedChatDetails.listing_id,
      title: currentSelectedChatDetails.listing_title,
      images: currentSelectedChatDetails.listing_image ? [currentSelectedChatDetails.listing_image] : [],
    };
  }, [currentSelectedChatDetails]);

  const handleSelectChat = (chatId: number) => {
    setSelectedChatId(chatId);
    setSearchParams({ chat: String(chatId) });
  };
  
  const handleBackToList = () => {
    setSelectedChatId(null);
    setSearchParams({}); // Clear chat from URL params
  };

  return {
    selectedChatId,
    handleSelectChat,
    handleBackToList,
    otherUserProfile,
    currentListing,
  };
};

// Hook for managing messages in a selected chat
const useMessages = (selectedChatId: number | null, user: any) => {
  const queryClient = useQueryClient();

  const { data: messages = [], isLoading: isLoadingMessages, refetch: refetchMessages } = useQuery({
    queryKey: ["messages", selectedChatId],
    queryFn: async (): Promise<Message[]> => {
      if (!selectedChatId) return [];
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("chat_id", selectedChatId)
        .order("created_at", { ascending: true });
      if (error) {
        console.error("Error fetching messages:", error);
        throw error;
      }
      return data || [];
    },
    enabled: !!selectedChatId,
    staleTime: 5000,
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (messageIds: number[]) => {
      if (!messageIds || messageIds.length === 0) return;
      const success = await markMessagesAsRead(messageIds);
      if (!success) throw new Error("Failed to mark messages as read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["messages", selectedChatId] });
      queryClient.invalidateQueries({ queryKey: ["unreadCount"] });
    },
  });

  useEffect(() => {
    if (selectedChatId && messages.length > 0 && user) {
      const unreadMessageIds = messages
        .filter(msg => !msg.is_read && msg.sender_id !== user.id)
        .map(msg => msg.id);
      if (unreadMessageIds.length > 0) {
        markAsReadMutation.mutate(unreadMessageIds);
      }
    }
  }, [selectedChatId, messages, user, markAsReadMutation]);

  return { messages, isLoadingMessages, refetchMessages };
};

// Hook for sending messages
const useSendMessage = (selectedChatId: number | null, user: any, invalidateChatList: () => void) => {
  const { toast } = useToast(); 
  const queryClient = useQueryClient();
  const [newMessage, setNewMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const sendMessageMutation = useMutation({
    mutationFn: async ({ content, imageUrl }: { content: string; imageUrl?: string; }) => {
      if (!selectedChatId || !user) throw new Error("Chat ose përdoruesi nuk u gjet");
      const { data, error } = await supabase
        .from("messages")
        .insert({ chat_id: selectedChatId, sender_id: user.id, content: content, image_url: imageUrl || null })
        .select();
      if (error) throw error;

      // Removed the attempt to update chats.updated_at as the column does not exist.
      // The chat list invalidation in onSuccess will handle re-sorting.

      return data;
    },
    onSuccess: () => {
      setNewMessage("");
      setSelectedImage(null);
      queryClient.invalidateQueries({ queryKey: ["messages", selectedChatId] });
      invalidateChatList(); // Invalidate chat list to reflect new message and order
    },
    onError: (error: any) => {
      toast({
        title: "Gabim gjatë dërgimit të mesazhit",
        description: error.message || "Ndodhi një gabim gjatë dërgimit të mesazhit tuaj.",
        variant: "destructive",
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (!validImageTypes.includes(file.type)) {
        toast({
          title: "Lloj i pavlefshëm i skedarit",
          description: "Ju lutemi zgjidhni një skedar imazhi (JPEG, PNG, GIF, WEBP).",
          variant: "destructive",
        });
        return;
      }
      if (file.size > maxSize) {
        toast({
          title: "Skedari shumë i madh",
          description: "Imazhi duhet të jetë më i vogël se 5MB.",
          variant: "destructive",
        });
        return;
      }
      setSelectedImage(file);
    }
  };

  const clearSelectedImage = () => setSelectedImage(null);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || !selectedChatId) return;

    setIsUploading(true);
    try {
      let imageUrl: string | undefined = undefined;
      if (selectedImage && user) {
        // Assuming 'message_image' is a valid AssetType for chat images
        imageUrl = await uploadProfileAssetToStorage(selectedImage, user.id, 'message_image', !!user);
      }
      sendMessageMutation.mutate({ content: newMessage.trim(), imageUrl });
    } catch (error) {
      console.error("Error uploading image or sending message:", error);
      toast({ title: "Gabim", description: "Dështoi dërgimi i mesazhit.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  return {
    newMessage,
    setNewMessage,
    selectedImage,
    setSelectedImage,
    isUploading,
    sendMessageMutation,
    handleFileChange,
    clearSelectedImage,
    handleSendMessage,
  };
};

// Hook for deleting messages and chats
const useDeleteChatActions = (selectedChatId: number | null, user: any) => {
  const { toast } = useToast(); 
  const queryClient = useQueryClient();
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [confirmChatDelete, setConfirmChatDelete] = useState(false);
  const [chatToDeleteId, setChatToDeleteId] = useState<number | null>(null);
  const navigate = useNavigate();
  // const [searchParams, setSearchParams] = useSearchParams(); // Not used currently

  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: number) => {
      if (!messageId) return false;
      const { error } = await supabase.from("messages").delete().eq("id", messageId);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      toast({ title: "Mesazhi u fshi", description: "Mesazhi është fshirë me sukses." });
      setMessageToDelete(null);
      setShowDeleteAlert(false);
      queryClient.invalidateQueries({ queryKey: ["messages", selectedChatId] });
      queryClient.invalidateQueries({ queryKey: ["chats", user?.id] }); // Re-fetch chat list
    },
    onError: (error: any) => {
      toast({
        title: "Gabim gjatë fshirjes së mesazhit",
        description: error.message || "Ndodhi një gabim gjatë fshirjes së mesazhit.",
        variant: "destructive",
      });
      setShowDeleteAlert(false);
    },
  });

  const deleteChatMutation = useMutation({
    mutationFn: async (chatId: number) => {
      if (!chatId) return false;
      const success = await deleteChat(chatId); // Assumes deleteChat is a server action
      if (!success) throw new Error("Dështoi fshirja e bisedës");
      return true;
    },
    onSuccess: () => {
      toast({
        title: "Biseda u fshi",
        description: "Biseda është fshirë me sukses.",
      });
      if (selectedChatId === chatToDeleteId) {
        // If the deleted chat was the selected one, navigate away or clear selection
        navigate('/messages'); // Or use handleBackToList if appropriate
      }
      setChatToDeleteId(null);
      setConfirmChatDelete(false);
      queryClient.invalidateQueries({ queryKey: ["chats", user?.id] }); // Re-fetch chat list
    },
    onError: (error: any) => {
      toast({
        title: "Gabim gjatë fshirjes së bisedës",
        description: error.message || "Ndodhi një gabim gjatë fshirjes së bisedës.",
        variant: "destructive",
      });
      setConfirmChatDelete(false);
    },
  });

  const handleDeleteMessage = (id: number) => {
    setMessageToDelete(id);
    setShowDeleteAlert(true);
  };

  const confirmDeleteMessage = () => {
    if (messageToDelete) {
      deleteMessageMutation.mutate(messageToDelete);
    }
  };

  const handleDeleteChat = (id: number) => {
    setChatToDeleteId(id);
    setConfirmChatDelete(true);
  };

  const confirmDeleteChat = () => {
    if (chatToDeleteId) {
      deleteChatMutation.mutate(chatToDeleteId);
    }
  };

  return {
    messageToDelete, // Not strictly needed to be returned if only used internally
    showDeleteAlert,
    setShowDeleteAlert,
    confirmChatDelete,
    setConfirmChatDelete,
    handleDeleteMessage,
    confirmDeleteMessage,
    handleDeleteChat,
    confirmDeleteChat,
  };
};

// Main hook that combines all chat functionalities
export const useChat = () => {
  const location = useLocation();
  const { user, isAuthenticated, isLoading: authIsLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast(); // Corrected path

  // Effect for handling authentication redirects
  useEffect(() => {
    if (!authIsLoading && !isAuthenticated && location.pathname === "/messages") {
      toast({
        title: "Kërkohet autentifikimi",
        description: "Ju lutemi identifikohuni për të parë mesazhet tuaja.",
        variant: "destructive",
      });
      navigate("/auth?redirect=/messages");
    }
  }, [authIsLoading, isAuthenticated, navigate, toast, location.pathname]);

  const { chatsData: chats, isLoadingChats, invalidateChatList } = useChatList(user, isAuthenticated);
  
  const {
    selectedChatId,
    handleSelectChat,
    handleBackToList,
    otherUserProfile,
    currentListing,
  } = useSelectedChat(chats || []);

  const { messages, isLoadingMessages } = useMessages(selectedChatId, user); // refetchMessages not used directly here

  const {
    newMessage,
    setNewMessage,
    selectedImage,
    setSelectedImage,
    isUploading,
    sendMessageMutation,
    handleFileChange, // This should be passed to MessageInput or used here
    clearSelectedImage,
    handleSendMessage,
  } = useSendMessage(selectedChatId, user, invalidateChatList);

  const {
    showDeleteAlert,
    setShowDeleteAlert,
    confirmChatDelete,
    setConfirmChatDelete,
    handleDeleteMessage,
    confirmDeleteMessage,
    handleDeleteChat,
    confirmDeleteChat,
  } = useDeleteChatActions(selectedChatId, user); // messageToDelete not used directly here

  // Formatting functions
  const formatMessageDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("sq-AL", { // Using Albanian locale for time
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatChatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    // Check if the date is today
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString("sq-AL", { // Albanian locale for time
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      // For other dates, show day and month
      return date.toLocaleDateString("sq-AL", { // Albanian locale for date
        day: "numeric",
        month: "short",
      });
    }
  };

  const handleViewListing = (listingId: number | undefined) => {
    if (listingId) {
      navigate(`/listing/${listingId}`);
    }
  };

  return {
    user,
    isAuthenticated,
    chats: chats || [],
    isLoadingChats,
    selectedChat: selectedChatId, // Renamed from selectedChatId for consistency if preferred
    setSelectedChat: handleSelectChat,
    newMessage,
    setNewMessage,
    otherUserProfile,
    currentListing,
    showDeleteAlert,
    setShowDeleteAlert,
    confirmChatDelete,
    setConfirmChatDelete,
    messages,
    isLoadingMessages,
    selectedImage,
    setSelectedImage,
    isUploading,
    sendMessageMutation, // Pass the mutation object itself
    handleSendMessage, // Pass the handler function
    handleFileChange, // Expose if MessageInput needs it directly, or handle internally
    clearSelectedImage,
    handleDeleteMessage,
    confirmDeleteMessage,
    handleDeleteChat,
    confirmDeleteChat,
    formatMessageDate,
    formatChatDate,
    handleBackToList,
    handleViewListing,
  };
};
